import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, Facebook, Instagram, Twitter } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import LegalModal from '../Legal/LegalModal';
import PrivacyModal from '../Legal/PrivacyModal';

const Footer: React.FC = () => {
  const { t, loadSpecificModule } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [showLegalModal, setShowLegalModal] = React.useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);

  const quickLinks = [
    { name: t('common.navigation.home'), href: '#home' },
    { name: t('common.navigation.services'), href: '#services' },
    { name: t('common.navigation.gallery'), href: '#gallery' },
    { name: t('common.navigation.faq'), href: '#faq' },
    { name: t('common.navigation.contact'), href: '#contact' },
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', name: 'Facebook' },
    { icon: Instagram, href: '#', name: 'Instagram' },
    { icon: Twitter, href: '#', name: 'Twitter' },
  ];

  const openLegalModal = async (e: React.MouseEvent) => {
    e.preventDefault();
    await loadSpecificModule('legal');
    setShowLegalModal(true);
  };

  const openPrivacyModal = async (e: React.MouseEvent) => {
    e.preventDefault();
    await loadSpecificModule('privacy');
    setShowPrivacyModal(true);
  };


  return (
    <>
      <footer className="bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-red-600 mb-4">
                {t('common.company.name')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <div className="text-gray-300">
                    <p>{t('common.company.address')}</p>
                    <p>{t('common.company.postalCode')} {t('common.company.city')}</p>
                    <p>{t('common.company.country')}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-red-600" />
                  <a
                    href={`tel:${t('common.company.phone')}`}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {t('common.company.phone')}
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-red-600" />
                  <a
                    href={`mailto:${t('common.company.email')}`}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {t('common.company.email')}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Opening Hours */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="text-lg font-semibold mb-4 flex items-center">
                <Clock className="w-5 h-5 text-red-600 mr-2" />
                {t('common.openingHours.title')}
              </h4>
              <div className="space-y-2 text-gray-300">
                <p>{t('common.openingHours.weekdays')}</p>
                <p>{t('common.openingHours.saturday')}</p>
                <p className="text-gray-400">{t('common.openingHours.sunday')}</p>
              </div>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="text-lg font-semibold mb-4">
                {t('common.footer.quickLinks')}
              </h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <motion.a
                      href={link.href}
                      whileHover={{ x: 5 }}
                      className="text-gray-300 hover:text-white transition-colors inline-block"
                    >
                      {link.name}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Social Links */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="text-lg font-semibold mb-4">
                {t('common.footer.followUs')}
              </h4>
              <div className="flex space-x-4">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-600 transition-all duration-200"
                      aria-label={social.name}
                    >
                      <Icon className="w-5 h-5" />
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
        >
          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6 mb-4 md:mb-0">
            <p className="text-gray-400 text-sm">
              {t('common.footer.copyright', { year: currentYear })}
            </p>
            <Link 
              to="/admin" 
              className="text-gray-500 hover:text-gray-400 text-xs transition-colors duration-200 no-underline"
            >
              - internal
            </Link>
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <span>{t('common.footer.poweredBy')}</span>
              {/* Debug: {JSON.stringify(translations.common?.footer)} */}
              <motion.a
                href="https://easylab.ai"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/easylab white.png" 
                  alt="Easylab AI" 
                  className="h-5 w-auto"
                />
              </motion.a>
            </div>
          </div>
          <div className="flex space-x-6">
            <motion.button
              onClick={openLegalModal}
              whileHover={{ color: '#fff' }}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t('common.footer.legal')}
            </motion.button>
            <motion.a
              href="#privacy"
              onClick={openPrivacyModal}
              whileHover={{ color: '#fff' }}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t('common.footer.privacy')}
            </motion.a>
            <motion.a
              href="#cookies"
              whileHover={{ color: '#fff' }}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t('common.footer.cookies')}
            </motion.a>
          </div>
        </motion.div>
      </div>
      </footer>
      
      <LegalModal 
        isOpen={showLegalModal} 
        onClose={() => setShowLegalModal(false)} 
      />
      <PrivacyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
    </>
  );
};

export default Footer;